import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { MailCheck } from 'lucide-react'
import { useState } from 'react'
import { Logo } from '@/components/brand/logo'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Field, Input } from '@/components/ui/field'
import { supabase } from '@/lib/supabase'

export const Route = createFileRoute('/reset-password')({
  // mode=link: opened from the reset email's link (the recovery session comes from the URL).
  validateSearch: (s: Record<string, unknown>): { mode?: 'link' } => ({
    mode: s.mode === 'link' ? 'link' : undefined,
  }),
  component: ResetPassword,
})

type Step = 'email' | 'sent' | 'code' | 'password'

/**
 * Password reset in clear steps: email, "check your email", then the new password.
 * The email carries a link (and, once the branded template is live, a 6-digit code too, for
 * reps who read mail on another device). The link always lands here so a password is set before
 * the app opens; app-wide, a PASSWORD_RECOVERY sign-in is also sent here (see main.tsx).
 */
function ResetPassword() {
  const navigate = useNavigate()
  const { mode } = Route.useSearch()
  const [step, setStep] = useState<Step>(mode === 'link' ? 'password' : 'email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const go = (s: Step) => {
    setError(null)
    setStep(s)
  }

  const send = async (e?: React.FormEvent) => {
    e?.preventDefault()
    setError(null)
    setBusy(true)
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: `${window.location.origin}/reset-password?mode=link`,
    })
    setBusy(false)
    if (error)
      return setError(
        /rate|seconds/i.test(error.message)
          ? 'Please wait a minute before asking for another email.'
          : error.message,
      )
    go('sent')
  }

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    const { error } = await supabase.auth.verifyOtp({
      email: email.trim().toLowerCase(),
      token: code.replace(/\s/g, ''),
      type: 'recovery',
    })
    setBusy(false)
    if (error) return setError('That code is wrong or has expired. Send a new email.')
    go('password')
  }

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password.length < 10) return setError('Use at least 10 characters.')
    if (password !== confirm) return setError('The two passwords do not match.')
    setBusy(true)
    const { error } = await supabase.auth.updateUser({ password })
    setBusy(false)
    if (error)
      return setError(
        /session|expired|missing/i.test(error.message)
          ? 'This reset link has expired. Send a new email.'
          : error.message,
      )
    await navigate({ to: '/' })
  }

  const alert = error && (
    <p role="alert" className="font-semibold text-danger">
      {error}
    </p>
  )

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-4 py-10">
      <div className="mb-8">
        <Logo on="theme" className="h-32" />
      </div>
      <Card className="w-full max-w-md p-6 sm:p-8">
        {step === 'email' && (
          <form onSubmit={send} className="flex flex-col gap-5">
            <h1 className="text-3xl uppercase">Reset password</h1>
            <p className="text-fg-muted">
              Enter your work email and we'll send you a link to set a new password.
            </p>
            <Field label="Email">
              {(p) => (
                <Input
                  {...p}
                  type="email"
                  required
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
            </Field>
            {alert}
            <Button type="submit" size="lg" block disabled={busy}>
              {busy ? 'Sending...' : 'Send reset email'}
            </Button>
          </form>
        )}

        {step === 'sent' && (
          <div className="flex flex-col gap-5">
            <MailCheck className="size-10 text-brand-text" aria-hidden />
            <h1 className="text-3xl uppercase">Check your email</h1>
            <p className="text-fg-muted">
              We sent a reset email to <strong className="text-fg">{email}</strong>. Open it on this
              device and tap <strong className="text-fg">Set a new password</strong>. It can take a
              few minutes; check your junk folder too.
            </p>
            {alert}
            <Button variant="outline" block onClick={() => go('code')}>
              My email has a 6-digit code
            </Button>
            <Button variant="ghost" block disabled={busy} onClick={() => void send()}>
              {busy ? 'Sending...' : 'Send it again'}
            </Button>
          </div>
        )}

        {step === 'code' && (
          <form onSubmit={verifyCode} className="flex flex-col gap-5">
            <h1 className="text-3xl uppercase">Enter your code</h1>
            <p className="text-fg-muted">Type the 6-digit code from the email to {email}.</p>
            <Field label="6-digit code">
              {(p) => (
                <Input
                  {...p}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={8}
                  required
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="text-center text-2xl tracking-[0.4em]"
                />
              )}
            </Field>
            {alert}
            <Button type="submit" size="lg" block disabled={busy}>
              {busy ? 'Checking...' : 'Continue'}
            </Button>
            <Button type="button" variant="ghost" onClick={() => go('sent')}>
              Back
            </Button>
          </form>
        )}

        {step === 'password' && (
          <form onSubmit={save} className="flex flex-col gap-5">
            <h1 className="text-3xl uppercase">Set a new password</h1>
            <p className="text-fg-muted">You'll use this to sign in from now on.</p>
            <Field label="New password" hint="At least 10 characters.">
              {(p) => (
                <Input
                  {...p}
                  type="password"
                  autoComplete="new-password"
                  required
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}
            </Field>
            <Field label="Confirm new password">
              {(p) => (
                <Input
                  {...p}
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              )}
            </Field>
            {alert}
            <Button type="submit" size="lg" block disabled={busy}>
              {busy ? 'Saving...' : 'Save and sign in'}
            </Button>
            {error && (
              <Button type="button" variant="ghost" onClick={() => go('email')}>
                Send a new email
              </Button>
            )}
          </form>
        )}

        <Link
          to="/login"
          className="mt-6 block text-center font-semibold text-brand-text underline underline-offset-4"
        >
          Back to sign in
        </Link>
      </Card>
    </div>
  )
}
